import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  StackView,
  Combobox,
  ComboboxOption,
  ComboboxMessage,
  ComboboxSectionTitle,
} from 'localmed-core';

import { Scaleup } from '../../../legacyCore/Transitions';
import { INSURANCE_COVERAGE_STATUS } from '../../constants';
import { widgetSettingsShape, withWidgetSettings } from '../WidgetSettings';
import GlobalInsuranceIssuerSelectWithData from './GlobalInsuranceIssuerSelectWithData';
import PartnerInsuranceIssuerSelect from './PartnerInsuranceIssuerSelect';

const COVERAGE_MESSAGE_TYPES = {
  [INSURANCE_COVERAGE_STATUS.UNKNOWN]: 'warning',
  [INSURANCE_COVERAGE_STATUS.CASH_PAYMENT]: null,
  [INSURANCE_COVERAGE_STATUS.OTHER]: null,
  [INSURANCE_COVERAGE_STATUS.IN_NETWORK]: 'success',
  [INSURANCE_COVERAGE_STATUS.WILL_FILE]: null,
  [INSURANCE_COVERAGE_STATUS.WILL_NOT_FILE]: 'error',
};

function issuerToValue(issuer) {
  return issuer ? issuer.id : '';
}

function issuerToLabel(issuer) {
  if (!issuer) {
    return '';
  }
  if (issuer.state && issuer.state !== 'US') {
    return `${issuer.display} (${issuer.state})`;
  }
  return issuer.display;
}

class InsuranceIssuerSelect extends Component {
  static propTypes = {
    selectedItem: PropTypes.shape({
      id: PropTypes.string.isRequired,
      display: PropTypes.string.isRequired,
    }),
    emptyLabel: PropTypes.string,
    placeholder: PropTypes.string,
    countryCode: PropTypes.string,
    widgetSettings: widgetSettingsShape,
    providerInsuranceCoverageData: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      providerInsuranceCoverage: PropTypes.shape({
        status: PropTypes.oneOf(Object.keys(INSURANCE_COVERAGE_STATUS)).isRequired,
        message: PropTypes.string.isRequired,
      }),
    }),
    onProviderInsuranceCoverageChange: PropTypes.func,
  };

  static defaultProps = {
    placeholder: 'Select an insurance…',
    countryCode: 'us',
  };

  state = {
    inputValue: (this.props.selectedItem && this.props.selectedItem.display) || '',
    isOpen: false,
  };

  componentDidUpdate(prevProps) {
    const { onProviderInsuranceCoverageChange } = this.props;
    if (!onProviderInsuranceCoverageChange) return;

    const data = prevProps.providerInsuranceCoverageData || {};
    const nextData = this.props.providerInsuranceCoverageData || {};

    if (nextData.providerInsuranceCoverage !== data.providerInsuranceCoverage) {
      onProviderInsuranceCoverageChange(nextData.providerInsuranceCoverage);
    }
  }

  onStateChange = ({ isOpen, inputValue }) => {
    const state = {};
    if (isOpen !== undefined) {
      state.isOpen = isOpen;
      if (isOpen) {
        state.inputValue = '';
      }
    }
    if (inputValue !== undefined) {
      state.inputValue = inputValue;
    }
    this.setState(state);
  };

  shouldUsePartnerInsuranceIssuerComponent() {
    const { widgetSettings: { partnerData } = {} } = this.props;
    if (partnerData == null) {
      return false;
    }
    const { loading, partner } = partnerData;
    if (loading) {
      return true;
    }
    if (partner == null) {
      return false;
    }
    return partner.insuranceIssuers.nodes.length > 0;
  }

  renderMessage() {
    const { providerInsuranceCoverageData: data, selectedItem } = this.props;
    if (!data || data.loading || !data.providerInsuranceCoverage) {
      return null;
    }
    const { status, message } = data.providerInsuranceCoverage;
    return (
      <Scaleup key={issuerToValue(selectedItem)} in>
        <Alert tier={COVERAGE_MESSAGE_TYPES[status]} fontSize="sm">
          {message}
        </Alert>
      </Scaleup>
    );
  }

  render() {
    const SelectComponent = this.shouldUsePartnerInsuranceIssuerComponent()
      ? PartnerInsuranceIssuerSelect
      : GlobalInsuranceIssuerSelectWithData;

    return (
      <StackView spacing="sm">
        <SelectComponent
          {...this.props}
          inputValue={this.state.inputValue}
          isOpen={this.state.isOpen}
          onStateChange={this.onStateChange}
          toLabel={issuerToLabel}
          toValue={issuerToValue}
          Combobox={Combobox}
          ComboboxOption={ComboboxOption}
          ComboboxMessage={ComboboxMessage}
          ComboboxSectionTitle={ComboboxSectionTitle}
        />
        {this.renderMessage()}
      </StackView>
    );
  }
}

export default withWidgetSettings()(InsuranceIssuerSelect);
